import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@devhop/ui";
import {
  parseFiltSearchParam,
  serializeFiltSearchParam,
} from "@/modules/forms/domain/lib/answer-filters-query";
import type { FormFieldDTO } from "@/modules/forms/presentation/api/types";
import { isFormFieldOptionEnabled } from "@/modules/forms/domain/lib/form-field-options";
import type { FormFieldOptionItem } from "@/server/platform/db/schema/forms";

const FILTER_TYPES = new Set<FormFieldDTO["type"]>([
  "choice",
  "dropdown",
  "checkbox",
]);

const CLEAR_SENTINEL = "__NONE__";

function fieldAllowsOtherDto(field: FormFieldDTO): boolean {
  const s = field.settings;
  return typeof s === "object" && s !== null && s.allowOther === true;
}

type FormResponsesAnswerFiltersBarProps = {
  fields: FormFieldDTO[];
  filtJson?: string;
  onNavigate: (next: { filt?: string; offset: number }) => void;
};

export function FormResponsesAnswerFiltersBar({
  fields,
  filtJson,
  onNavigate,
}: FormResponsesAnswerFiltersBarProps) {
  const parsed = parseFiltSearchParam(filtJson);
  const filterable = [...fields]
    .filter((f) => FILTER_TYPES.has(f.type))
    .sort((a, b) => a.order - b.order);

  if (filterable.length === 0) return null;

  const filterFieldIds = new Set(filterable.map((f) => f.id));
  const hasFilters = Object.entries(parsed).some(
    ([k, v]) =>
      filterFieldIds.has(k) && typeof v === "string" && v.trim().length > 0,
  );

  function patchFilter(fieldId: string, raw: string) {
    const next = { ...parsed };
    if (!raw.trim() || raw === CLEAR_SENTINEL) delete next[fieldId];
    else next[fieldId] = raw.trim();

    const ser = serializeFiltSearchParam(next);
    onNavigate({ filt: ser, offset: 0 });
  }

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-sm">ກອງຕາມຄຳຖາມທາງເລືອກ</CardTitle>
        {hasFilters ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={() => onNavigate({ filt: undefined, offset: 0 })}
          >
            ລ້າງຕົວກອງ
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-4">
          {filterable.map((field) => {
            const opts = (field.options ?? []) as FormFieldOptionItem[];
            const normalizedCur = (parsed[field.id] ?? "").trim();
            const hasOptionMatch = opts.some((o) => o.value === normalizedCur);
            const orphan =
              normalizedCur.length > 0 &&
              !hasOptionMatch &&
              fieldAllowsOtherDto(field);

            const selectVal =
              normalizedCur.length === 0
                ? CLEAR_SENTINEL
                : !hasOptionMatch && !orphan
                  ? CLEAR_SENTINEL
                  : normalizedCur;

            return (
              <div
                key={field.id}
                className="flex min-w-40 max-w-80 flex-col gap-1.5"
              >
                <Label className="text-muted-foreground text-xs">
                  {field.label}
                </Label>
                <Select
                  value={selectVal}
                  onValueChange={(v) => patchFilter(field.id, v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="ທັງໝົດ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={CLEAR_SENTINEL}>ທັງໝົດ</SelectItem>
                    {opts.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                        {!isFormFieldOptionEnabled(o) ? " (ປິດ)" : ""}
                      </SelectItem>
                    ))}
                    {orphan ? (
                      <SelectItem value={normalizedCur}>
                        ອື່ນ: {normalizedCur}
                      </SelectItem>
                    ) : null}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
